import {
  combineReducers
} from 'redux'
import reduceReducers from 'reduce-reducers'
import {
  createReducer,
  createNamedReducer
} from 'helpers/ReducerUtil'
import * as R from 'ramda'
import P from 'helpers/predicates'

import {
  CLEAR_FILTERS,
  UPDATE_TOTAL,
  UPDATE_INPUT_VALUE,
  UPDATE_INPUT_FILTER_VALUE,
  UPDATE_FIELD_FILTER_VALUE,
  CLEAR_FIELD_FILTERS,
  UPDATE_FIELD_FILTERS,
  UPDATE_SIZE,
  REMOVE_FILTERS,
  INITIALIZE_FILTERS,
  INITIALIZE_HEADER_FILTERS,
  UPDATE_FILTERS,
  UPDATE_FROM,
  UPDATE_ACTIVE,
  UPDATE_PINNED,
  UPDATE_ROW_SIZE,
  UPDATE_MAX_ROW_SIZE,
  UPDATE_MENU_ACTIVE,
  UPDATE_CURRENT_INDEX,
  UPDATE_SORT_BY,
  UPDATE_SORT_TYPE,
  UPDATE_SORT_INDEX,
  UPDATE_CUSTOM_COL_ORDER,
  UPDATE_INPUT_HEADER_LIST,
  CLEAR_HEADER_FILTERS,
  UPDATE_HEADER_FILTER,
  REMOVE_HEADER_FILTER,
  FROM_SIZE,
  UPDATE_LIST_ORDER,
  CLEAR_CUSTOM_COL_ORDER,
  listOrder,
  COPY_DISPLAY_FILTERS,
  ON_SELECT_SEARCH_FILTER,
  ON_REMOVE_SEARCH_FILTER,

  ProductHierarchyEnum,
  ON_SELECT_PRODUCT_FILTER,
  UPDATE_PRODUCT_HIERARCHY_SOURCE,
  UPDATE_PRODUCT_HIERARCHY_FILTER,
  AUTO_UPDATE_PRODUCT_HIERARCHY_SOURCE,
  UPDATE_SEARCH_FILTERS,
  TOGGLE_COLLAPSE,
  defaultFields,
  defaultSelectedSearchFields,
  CLEAR,

  UPDATE_PUBLIC_CUSTOM_VIEWS,
  UPDATE_CUSTOM_VIEW,
  UPDATE_VIEW_NAME,
  TOGGLE_PUBLIC_VIEW,
  UPDATE_TEMP_CUSTOM_VIEW,
  UPDATE_TEMP_SEARCH_FILTERS,
  UPDATE_TEMP_PRODUCT_FILTERS,
  UPDATE_TEMP_SELECTED_PRODUCT_FILTERS,
  UPDATE_TEMP_SELECTED_SEARCH_FILTERS,
  
  SELECT_TEMP_SEARCH_FILTER,
  REMOVE_TEMP_SEARCH_FILTER,
  SELECT_TEMP_PRODUCT_FILTER,
  REMOVE_TEMP_PRODUCT_FILTER,

  ON_SELECT_PUBLIC_FILTER,
  ON_REMOVE_PUBLIC_FILTER,
  DELETE_CUSTOM_VIEW,
  EDIT_CUSTOM_VIEW,
  CLEAR_VIEW_LABEL,
  SET_DEFAULT_VIEW,
  TOGGLE_SEARCH_SECTION_COLLAPSE,
  CLEAR_SEARCH_SECTION_COLLAPSES,
  REMOVE_CUSTOM_VIEW,
  DESELECT_ALL_SEARCH_FILTERS,
  SELECT_ALL_SEARCH_FILTERS,
  DefaultSearchFields,
  CLEAR_SEARCH_FILTERS_UI,
  COPY_HEADER_FILTERS,
  COLLAPSE_ALL_SEARCH_FILTERS,
  EXPAND_ALL_SEARCH_FILTERS,
  SELECT_ALL_SEARCH_SECTION,
  UPDATE_SELECTED_SEARCH_FILTERS,
  ADD_TO_SEARCH_FILTERS,
  COPY_PRODUCT_HIERARCHY
} from './constants'
import _ from 'lodash'

const appendUnique = R.curry((val, list) => R.compose(
  R.uniq,
  R.append
)(val, list))

const concatUnique = R.curry((val, list) => R.compose(
  R.uniq,
  R.concat
)(val, list))

const prependUnique = R.curry((val, list) => R.compose(
  R.uniq,
  R.prepend
)(val, list))

const updateActive = (prev, { active }) => P.isNotNil(active)
  ? active
  : prev

const updateTotal = (prev, { value }) => P.isNotNil(value)
  ? value
  : prev

const updateMaxRowSize = (prev, { size }) => P.isNotNil(size)
  ? size
  : prev

const updateInputValue = (prev, { value }) => P.isNotNil(value)
  ? value
  : prev

const updateFrom = (prev, { from }) => P.isNotNil(from)
  ? from
  : prev

const updateSize = (prev, { size }) => P.isNotNil(size)
  ? size
  : prev

const updatePinned = (prev, { enable }) => P.isNotNil(enable)
  ? enable
  : prev

const updateRowSize = (prev, { size }) => {
  if (size !== 'View all') {
    size = parseInt(size)
  }
  return (P.isNotNil(size) ? size : prev)
}

const updateMenuActive = (prev, { active }) => P.isNotNil(active)
  ? active
  : prev

const updateListOrder = (prev, { listOrder }) => P.isNotNil(listOrder)
  ? listOrder
  : prev

const updateCustomColOrder = (prev, { colOrder }) => P.isNotNil(colOrder)
  ? colOrder
  : prev

const updateCurrentIndex = (prev, { columnIndex }) => columnIndex !== undefined
  ? columnIndex
  : null

const updateSortType = (prev, { value }) => P.isNotNil(value)
  ? value
  : prev

const updateSortBy = (prev, { value }) => P.isNotNil(value)
  ? value
  : prev

const updateSortIndex = (prev, { index }) => P.isNotNil(index)
  ? index
  : prev

const updateHeaderFilter = (prev, { filterType, filter }) =>
  R.and(P.isNotNil(filterType), P.isNotEmptyOrNil(filter))
    ? R.compose(
        R.assoc(filterType, R.__, prev),
        R.compose(
          R.uniq,
          R.append(R.__, R.prop(filterType, prev)),
        )
      )(filter)
    : prev

const removeHeaderFilter = (prev, { filterType, filter }) =>
  R.and(P.isNotNil(filterType), P.isNotEmptyOrNil(filter))
    ? R.compose(
        R.assoc(filterType, R.__, prev),
        R.without(R.__, R.prop(filterType, prev))
      )([filter])
    : prev

const initializeFilters = (prev, { filters }) => P.isNotNil(filters)
  ? filters
  : prev

const initializeHeaderFilters = (prev, { filters }) => P.isNotNil(filters)
  ? filters
  : prev

const updateFilters = (prev, {filter}) => {
  let f = P.isNotNil(filter)
    ? appendUnique(filter)(prev)
    : prev
  return f
}

const updateSource = (prev, { source }) => {
  return (source ? [...source] : prev)
}

const updateSearchFilters = (prev, { filters }) => {
  // TODO update this to match instead of having this if condition
  if (filters.data) 
    return (filters.data ? [...filters.data] : prev)
  else
    return (filters ? [...filters] : prev)
}

const addToSearchFilters = (prev, { filters }) => {
  return concatUnique(filters)(prev)
}

const updateSelectedSearchFilters = (prev, { filters }) => {
  return concatUnique(filters)(prev)
}

const updateProductValue = (inputs, action) => {
  let arr = [...inputs]
  if (arr.includes(action.filters[0])){
      arr.splice(arr.indexOf(action.filters[0]), 1)
  } else {
      arr.push(action.filters[0])
  }
  return arr
}

const autoUpdateSource = (filter, {source}) => {
  let arr = [...filter]
  if (!arr.includes(source[0])) {
      arr.push(source[0])
  }
  return arr
}

const removeCustomView = () => {
  return ''
}

const removePublicView = () => {
  return ''
}

const newCustomView = (prev, {name}) => name

const toggleCollapse = (isCollapse) => {
  return(!isCollapse)
}

const updateArrayOfFilters = (prev, { filters }) => {
  if (P.isNotNil(filters)) {
    return prependUnique('id')(filters)
  } else {
    return prev
  }
}

const updateAllHeaderFilters = (prev, {filters}) => P.isNotNil(filters)
  ? filters
  : prev


const removeFilters = (prev, { filter }) => P.isNotNil(filter)
  ? R.without([filter])(prev)
  : prev

const updateInputHeaderList = (prev, { filters }) => P.isNotNil(filters)
  ? filters
  : prev

const updateQuickFilter = (prev, { filter }) => {
  return (filter || filter === 0 ? [...prev, filter] : prev)
}

const removeQuickFilter = (prev, { filter }) => P.isNotNil(filter)
  ? R.without([filter])(prev)
  : prev

const updateCustomView = (prev, {view}) => P.isNotNil(view)
  ? view
  : prev

const setDefaultView = (prev, {idx}) => P.isNotNil(idx)
  ? idx
  : prev

const togglePublicView = (action) => !action

const updatePublicCustomViews = (prev, { data }) => P.isNotNil(data)
  ? data
  : prev

const updateTempCustomView = (prev, {view}) => P.isNotNil(view)
  ? view
  : prev

const updateTempSearchFilters = (prev, { filters }) => P.isNotNil(filters)
  ? filters
  : prev

const updateTempProductFilters = (prev, { filters }) => P.isNotNil(filters)
  ? filters
  : prev

const updateSelectedTempProductFilters = (prev, { filters }) => P.isNotNil(filters)
  ? filters
  : prev

const updateSelectedTempSearchFilters = (prev, { filters }) => P.isNotNil(filters)
  ? filters
  : prev

const selectTempFilter = (prev, { filter }) => P.isNotNil(filter)
  ? appendUnique(filter)(prev)
  : prev

const removeTempFilter = (prev, { filter }) => P.isNotNil(filter)
  ? R.without([filter])(prev)
  : prev

const selectTempSearchFilter = (prev, { filter }) => P.isNotNil(filter)
  ? appendUnique(filter)(prev)
  : prev

const removeTempSearchFilter = (prev, { filter }) => P.isNotNil(filter)
  ? R.without([filter])(prev)
  : prev

const selectPublicFilter = (prev, { filter }) => P.isNotNil(filter)
  ? filter
  : prev

const toggleSearchSectionCollapses = (prev, { idx }) => {
  if (prev.indexOf(idx) === -1) {
    return appendUnique(idx)(prev)
  } else {
    return R.without([idx])(prev)
  }
}

const expandSearchFilters = (prev, {length}) => {
  // this is funny, i bet tehres a better way to do this right now
  let i = 0
  let arr = []
  while(i < length) {
    arr.push(i)
    i++
  }
  return arr
}

const collapseSearchFilters = (prev) => {
  return []
}

const updateSectionOfFilters = (prev, { filters }) => {
  let missing = false
  filters.forEach((filter) => {
    if (!prev.includes(filter)) {
      missing = true
    }
  })
  if (missing) {
    return concatUnique(filters)(prev)
  } else {
    return R.without(filters)(prev)
  }
}

const copyProductHierarchy = (prev, { filters }) => {
  return _.assign(prev, filters)
}

const clearSearchSectionCollapses = (prev) => {
  return []
}

const clearFilters = (prev) => {
  return {
    ...prev,
    [ProductHierarchyEnum.SEGMENT.label]: [],
    [ProductHierarchyEnum.LINE.label]: [],
    [ProductHierarchyEnum.FAMILY.label]: [],
    [ProductHierarchyEnum.PLATFORM.label]: [],
    [ProductHierarchyEnum.SKU.label]: [],
    [ProductHierarchyEnum.BRAND.label]: [],
  }
}
const inputValueReducer = createReducer('', {
  [UPDATE_INPUT_VALUE]: updateInputValue,
})

const fromReducer = createReducer(0, {
  [UPDATE_FROM]: updateFrom
})

const sizeReducer = createReducer(25, {
  [UPDATE_SIZE]: updateSize
})

const totalReducer = createReducer(0, {
  [UPDATE_TOTAL]: updateTotal
})

const maxRowSizeReducer = createReducer(0, {
  [UPDATE_MAX_ROW_SIZE]: updateMaxRowSize
})

const filtersReducer = createReducer([], {
  // [CLEAR_FILTERS]: () => [],
  [INITIALIZE_FILTERS]: initializeFilters,
  [UPDATE_SEARCH_FILTERS]: updateFilters,
  [REMOVE_FILTERS]: removeFilters
})

const fieldFiltersReducer = createReducer([], {
  [CLEAR_FIELD_FILTERS]: () => [],
  [UPDATE_FIELD_FILTERS]: updateArrayOfFilters
})


// const searchFiltersReducer = createReducer([], {
//   [CLEAR_SEARCH_FILTERS]: () => [],
//   [UPDATE_SEARCH_FILTERS_SOURCE]: updateArrayOfFilters
// })

const searchFiltersReducer = createReducer(defaultSelectedSearchFields, {
  [CLEAR_FILTERS]: () => defaultSelectedSearchFields,
  [ON_SELECT_SEARCH_FILTER]: updateFilters,
  [ON_REMOVE_SEARCH_FILTER]: removeFilters,
  [COPY_DISPLAY_FILTERS]: updateArrayOfFilters,
  [DESELECT_ALL_SEARCH_FILTERS]: () => ['id'],
  [SELECT_ALL_SEARCH_FILTERS]: updateArrayOfFilters,
  [SELECT_ALL_SEARCH_SECTION]: updateSectionOfFilters,
  [UPDATE_SELECTED_SEARCH_FILTERS]: updateSelectedSearchFilters
})

const inputFilterValueReducer = createReducer('', {
  [UPDATE_INPUT_FILTER_VALUE]: updateInputValue,
})

const fieldFilterValueReducer = createReducer('', {
  [UPDATE_FIELD_FILTER_VALUE]: updateInputValue,
})

const activeReducer = createReducer(false, {
  [UPDATE_ACTIVE]: updateActive
})

const pinnedDrawerReducer = createReducer(false, {
  [UPDATE_PINNED]: updatePinned
})

const rowSizeReducer = createReducer(FROM_SIZE, {
  [UPDATE_ROW_SIZE]: updateRowSize
})

const menuActiveReducer = createReducer(false, {
  [UPDATE_MENU_ACTIVE]: updateMenuActive
})

const columnIndexReducer = createReducer(null, {
  [UPDATE_CURRENT_INDEX]: updateCurrentIndex
})

const listOrderReducer = createReducer(listOrder, {
  [CLEAR_CUSTOM_COL_ORDER]: R.always(listOrder),
  [UPDATE_LIST_ORDER]: updateListOrder
})

const customColOrderReducer = createReducer(false, {
  [CLEAR_CUSTOM_COL_ORDER]: () => false,
  [UPDATE_CUSTOM_COL_ORDER]: updateCustomColOrder
})

const headerFiltersReducer = createReducer({}, {
  [CLEAR_HEADER_FILTERS]: R.always({}),
  [INITIALIZE_HEADER_FILTERS]: initializeHeaderFilters,
  [UPDATE_HEADER_FILTER]: updateHeaderFilter,
  [REMOVE_HEADER_FILTER]: removeHeaderFilter,
  [COPY_HEADER_FILTERS]: updateAllHeaderFilters
})

const sortIndexReducer = createReducer(0, {
  [CLEAR_HEADER_FILTERS]: R.always(0),
  [UPDATE_SORT_INDEX]: updateSortIndex
})

const sortTypeReducer = createReducer('id', {
  [CLEAR_HEADER_FILTERS]: R.always('id'),
  [UPDATE_SORT_TYPE]: updateSortType
})

const viewsReducer = createReducer('', {
  [UPDATE_VIEW_NAME]: newCustomView,
  [CLEAR_VIEW_LABEL]: () => ''
})

const sortByReducer = createReducer('desc', {
  [CLEAR_HEADER_FILTERS]: R.always('desc'),
  [UPDATE_SORT_BY]: updateSortBy
})

const inputHeaderSourceReducer = createReducer([], {
  [UPDATE_INPUT_HEADER_LIST]: updateInputHeaderList
})

const customViewsReducer = createReducer('', {
  [UPDATE_CUSTOM_VIEW]: updateCustomView,
  [REMOVE_CUSTOM_VIEW]: removeCustomView
})

const makePublicViewReducer = createReducer(false, {
  [TOGGLE_PUBLIC_VIEW]: togglePublicView
})

const tempCustomViewReducer = createReducer('', {
  [UPDATE_TEMP_CUSTOM_VIEW]: updateTempCustomView,
  [DELETE_CUSTOM_VIEW]: updateTempCustomView,
  [EDIT_CUSTOM_VIEW]: updateTempCustomView
})

const productHierarchySourceSliceReducer = createReducer([], {
  [UPDATE_PRODUCT_HIERARCHY_SOURCE]: updateSource,
})

const productHierarchyFilterSliceReducer = createReducer([], {
  [UPDATE_PRODUCT_HIERARCHY_FILTER]: updateFilters,
  [UPDATE_PRODUCT_HIERARCHY_SOURCE]: updateSource,
})

const fullProductHierarchyReducer = createReducer([], {
  [COPY_PRODUCT_HIERARCHY]: copyProductHierarchy
})

const searchFormElementsReducer = createReducer([], {
  [UPDATE_SEARCH_FILTERS]: updateSearchFilters,
  [ADD_TO_SEARCH_FILTERS]: addToSearchFilters,
  [CLEAR_SEARCH_FILTERS_UI]: () => DefaultSearchFields
})

const updateInputValueSliceReducer = createReducer([], {
  [ON_SELECT_PRODUCT_FILTER]: updateProductValue,
  [AUTO_UPDATE_PRODUCT_HIERARCHY_SOURCE]: autoUpdateSource
})

const clearProductHierarchyUiStateReducer = createReducer({}, {
  [CLEAR]: clearFilters
})

const collapseSliceReducer = createReducer(false, {
  // [TOGGLE_COLLAPSE]: toggleCollapse,
})

const cphReducer = createReducer(false, {
  [TOGGLE_COLLAPSE]: toggleCollapse
})

const publicCustomViewReducer = createReducer([], {
  [UPDATE_PUBLIC_CUSTOM_VIEWS]: updatePublicCustomViews
})

const tempProductFiltersReducer = createReducer([], {
  [UPDATE_TEMP_PRODUCT_FILTERS]: updateTempProductFilters
})

const tempSearchFiltersReducer = createReducer([], {
  [UPDATE_TEMP_SEARCH_FILTERS]: updateTempSearchFilters
})

const tempSelectedProductFiltersReducer = createReducer([], {
  [UPDATE_TEMP_SELECTED_PRODUCT_FILTERS]: updateSelectedTempProductFilters,
  [SELECT_TEMP_PRODUCT_FILTER]: selectTempFilter,
  [REMOVE_TEMP_PRODUCT_FILTER]: removeTempFilter
})

const tempSelectedSearchFiltersReducer = createReducer([], {
  [UPDATE_TEMP_SELECTED_SEARCH_FILTERS]: updateSelectedTempSearchFilters,
  [SELECT_TEMP_SEARCH_FILTER]: selectTempSearchFilter,
  [REMOVE_TEMP_SEARCH_FILTER]: removeTempSearchFilter
})

const publicViewReducer = createReducer('', {
  [ON_SELECT_PUBLIC_FILTER]: selectPublicFilter,
  [ON_REMOVE_PUBLIC_FILTER]: removePublicView
})

const defaultViewReducer = createReducer('', {
  [SET_DEFAULT_VIEW]: setDefaultView
})

const collapsedSearchSectionReducer = createReducer([], {
  [TOGGLE_SEARCH_SECTION_COLLAPSE]: toggleSearchSectionCollapses,
  [CLEAR_SEARCH_SECTION_COLLAPSES]: clearSearchSectionCollapses,
  [EXPAND_ALL_SEARCH_FILTERS]: expandSearchFilters,
  [COLLAPSE_ALL_SEARCH_FILTERS]: collapseSearchFilters
})


const productHierarchySliceReducer = combineReducers({
  source: productHierarchySourceSliceReducer,
  filters: productHierarchyFilterSliceReducer
})

const productHierarchyReducer = (() => {
  const reducers = Object.keys(ProductHierarchyEnum)
      .reduce((result, key) => {
          const name = ProductHierarchyEnum[key].label
          let reducer = createNamedReducer(name, productHierarchySliceReducer)
          return {
              ...result,
              [name]: reducer
          }
      }, {})
  return reduceReducers(combineReducers(reducers),
    fullProductHierarchyReducer
  )
})()

const productHierarchyUiStateReducer = (() => {
  const createReducerWithName = createNamedReducer('ProductHierarchy')
  const reducers = Object.keys(ProductHierarchyEnum)
      .reduce((result, key) => {
          const name = ProductHierarchyEnum[key].label
          return {
              ...result,
              [name]: createNamedReducer(name, updateInputValueSliceReducer)
          }
      }, {})
  return reduceReducers(
      combineReducers({
          isCollapse: createReducerWithName(collapseSliceReducer),
          ...reducers,
      }),
      clearProductHierarchyUiStateReducer
  )
})()


export default combineReducers({
  inputValue: inputValueReducer,
  from: fromReducer,
  size: sizeReducer,
  filters: filtersReducer,
  fieldFilters: fieldFiltersReducer,
  searchFilters: searchFiltersReducer,
  publicCustomView: publicCustomViewReducer,
  total: totalReducer,
  maxRowSize: maxRowSizeReducer,
  inputFilterValue: inputFilterValueReducer,
  fieldFilterValue: fieldFilterValueReducer,
  active: activeReducer,
  pinnedDrawer: pinnedDrawerReducer,
  rowSize: rowSizeReducer,
  menuActive: menuActiveReducer,
  columnIndex: columnIndexReducer,
  listOrder: listOrderReducer,
  customColOrder: customColOrderReducer,
  headerFilters: headerFiltersReducer,
  sortType: sortTypeReducer,
  sortBy: sortByReducer,
  sortIndex: sortIndexReducer,
  inputHeaderSource: inputHeaderSourceReducer,
  customView: customViewsReducer,
  productHierarchy: productHierarchyReducer,
  searchFormElements: searchFormElementsReducer,
  productHierarchyUiState: productHierarchyUiStateReducer,
  viewLabel: viewsReducer,
  makePublicView: makePublicViewReducer,
  collapsedSearchSections: collapsedSearchSectionReducer,
  tempCustomView: tempCustomViewReducer,
  tempProductFilters: tempProductFiltersReducer,
  tempSearchFilters: tempSearchFiltersReducer,
  tempSelectedProductFilters: tempSelectedProductFiltersReducer,
  tempSelectedSearchFilters: tempSelectedSearchFiltersReducer,
  publicView: publicViewReducer,
  collapseProductHierarchy: cphReducer,
  defaultView: defaultViewReducer
})
